/**
 * piDockStore — "Pi chat on all tabs" P1 (2026-07-03).
 *
 * The Pi chat dock is the ONE hoisted Pi/legacy chat instance, rendered once at
 * the App level (sibling of TriShellLayout) as an always-mounted collapsible
 * overlay drawer so it is reachable on every view — not just Designer. This
 * store holds its open/collapsed state + width.
 *
 * Dedicated store (NOT uiStore) on purpose: uiStore's `partialize` is a tightly
 * guarded contract with exact-key assertions across many tests; adding fields
 * there is high blast-radius. This mirrors chatHarnessStore / chatPrefsStore —
 * small, self-contained, persisted per-browser. (Worldview §6 D-C alternative.)
 *
 * Persisted so the user's open/collapsed choice + width survive reload. Default
 * open=true preserves the historical Designer always-on-chat feel while now
 * being present on every tab.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Dock width in px. Min keeps the composer usable; default ≈ the old 28% chat
// column. There is NO static max — the user drags the LEFT edge to widen the
// dock as far as they want, bounded only by leaving the left panels at least
// MIN_LEFT_PANELS_WIDTH (operator 2026-07-04, Cursor-IDE-style resize).
export const PI_DOCK_WIDTH_RANGE = {
  min: 280,
  default: 380,
} as const;

// The left panels (shells + voice) never get narrower than this — so the dock's
// effective max is the viewport minus this floor.
export const MIN_LEFT_PANELS_WIDTH = 360;

/** Viewport-dynamic max dock width. Never returns below the dock's own min. */
export function maxDockWidth(
  viewportWidth: number = typeof window !== 'undefined' ? window.innerWidth : 1280,
): number {
  return Math.max(
    PI_DOCK_WIDTH_RANGE.min,
    Math.round(viewportWidth - MIN_LEFT_PANELS_WIDTH),
  );
}

function clampWidth(n: number): number {
  return Math.max(
    PI_DOCK_WIDTH_RANGE.min,
    Math.min(maxDockWidth(), Math.round(n)),
  );
}

interface PiDockState {
  open: boolean;
  width: number;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  setWidth: (width: number) => void;
  reset: () => void;
}

/**
 * partialize — the persist contract. Only `open` + `width` are durable; the
 * setters are not. Exported so the contract is unit-testable.
 */
export const piDockStorePartialize = (
  state: PiDockState,
): { open: boolean; width: number } => ({
  open: state.open,
  width: state.width,
});

export const usePiDockStore = create<PiDockState>()(
  persist(
    (set, get) => ({
      open: true,
      width: PI_DOCK_WIDTH_RANGE.default,
      setOpen: (open) => set({ open }),
      toggle: () => set({ open: !get().open }),
      setWidth: (width) => set({ width: clampWidth(width) }),
      reset: () => set({ open: true, width: PI_DOCK_WIDTH_RANGE.default }),
    }),
    {
      name: 'vibe-designer-pi-dock',
      partialize: piDockStorePartialize,
      // Defensive coercion of a corrupt persisted payload (older schema, manual
      // localStorage edit, out-of-range width) so first paint is always sane.
      onRehydrateStorage: () => (state) => {
        if (state) {
          const s = state as PiDockState;
          s.width =
            typeof s.width === 'number' && Number.isFinite(s.width)
              ? clampWidth(s.width)
              : PI_DOCK_WIDTH_RANGE.default;
          if (typeof s.open !== 'boolean') s.open = true;
        }
      },
    },
  ),
);
